// AI Signals Dashboard Page
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Brain, 
  Activity, 
  TrendingUp, 
  Shield, 
  Target, 
  BarChart3, 
  RefreshCw,
  Settings,
  Bell,
  BellOff,
  Play,
  Pause,
  Eye,
  Grid3X3,
  List,
  Zap
} from "lucide-react";
import { PageLayout } from "@/components/layout/PageLayout";
import { SignalCard } from "@/components/ai-signals/SignalCard";
import { SignalTable } from "@/components/ai-signals/SignalTable";
import {
  SignalHistoryChart,
  ModelPerformanceChart,
  SignalDistributionChart,
  RiskMetricsChart,
  SignalPerformanceSummary,
  WinRateByConfidenceChart,
  CumulativeReturnsChart,
  SignalPerformanceOverTimeChart
} from "@/components/ai-signals/SignalCharts";
import {
  useAISignals,
  useModelPerformance,
  useRiskMetrics,
  useSignalStats,
  useSignalFilters,
  useSignalExecution
} from "@/hooks/use-ai-signals";
import { useWallet } from "@/contexts/WalletContext";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

export default function AISignals() {
  // State management
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  
  // Filters and sorting
  const { 
    filters, 
    sortBy, 
    sortOrder, 
    updateFilter, 
    clearFilters, 
    setSort 
  } = useSignalFilters();
  
  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Data fetching hooks
  const {
    signals,
    totalCount,
    hasNext,
    hasPrev,
    isLoading,
    error,
    refetch,
    isSubscribed,
    connectionStatus,
    isConnecting
  } = useAISignals(filters, page, perPage, sortBy, sortOrder, autoRefresh);
  
  const { models, isLoading: modelsLoading } = useModelPerformance();
  const { riskMetrics, isLoading: riskLoading } = useRiskMetrics();
  const { stats, isLoading: statsLoading } = useSignalStats();
  const { executeSignal, cancelSignal } = useSignalExecution();
  const { publicKey } = useWallet();


  // Handle signal execution
  const handleExecuteSignal = async (signalId: string) => {
    try {
      const result = await executeSignal(signalId, publicKey || undefined);
      if (result.success) {
        toast({
          title: "Signal Executed",
          description: "AI signal has been executed successfully",
        });
        refetch();
      } else {
        toast({
          title: "Signal Execution Failed",
          description: result.error || "Failed to execute AI signal",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error executing signal:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to execute signal";
      toast({
        title: "Signal Execution Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Handle signal cancellation
  const handleCancelSignal = async (signalId: string) => {
    try {
      const result = await cancelSignal(signalId);
      if (result.success) {
        toast({
          title: "Signal Cancelled",
          description: "AI signal has been cancelled",
        });
        refetch();
      } else {
        toast({
          title: "Signal Cancellation Failed",
          description: result.error || "Failed to cancel AI signal",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error cancelling signal:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to cancel signal";
      toast({
        title: "Signal Cancellation Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle sort change
  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSort(newSortBy, newSortOrder);
    setPage(1); // Reset to first page when sorting
  };

  // Handle filters change
  const handleFiltersChange = (newFilters: any) => {
    updateFilter('symbol', newFilters.symbol);
    updateFilter('signal_type', newFilters.signal_type);
    updateFilter('confidence_level', newFilters.confidence_level);
    updateFilter('status', newFilters.status);
    setPage(1); // Reset to first page when filtering
  };

  if (error) {
    return (
      <>
        <Helmet><title>AI Signals - Error</title></Helmet>
        <PageLayout>
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Brain className="w-16 h-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Failed to Load AI Signals</h2>
            <p className="text-muted-foreground mb-4 text-center max-w-md">
              {error instanceof Error ? error.message : 'An error occurred while loading the AI signals dashboard.'}
            </p>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI Signals - TheMoonRun</title>
        <meta name="description" content="AI-powered trading signals with real-time analysis and recommendations." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Brain className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-display font-bold text-foreground">AI Signals</h1>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className={cn(
                        "text-xs",
                        connectionStatus === 'connected' ? "text-success border-success/20 bg-success/10" :
                        connectionStatus === 'reconnecting' ? "text-warning border-warning/20 bg-warning/10" :
                        signals.length > 0 ? "text-warning border-warning/20 bg-warning/10" :
                        "text-destructive border-destructive/20 bg-destructive/10"
                      )}>
                        {connectionStatus === 'connected' ? (
                          <>
                            <Activity className="w-3 h-3 mr-1" />
                            Live
                          </>
                        ) : connectionStatus === 'reconnecting' ? (
                          <>
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            Reconnecting...
                          </>
                        ) : signals.length > 0 ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Cached
                          </>
                        ) : (
                          <>
                            <Pause className="w-3 h-3 mr-1" />
                            Offline
                          </>
                        )}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {connectionStatus === 'connected'
                          ? "Connected to live AI signals via WebSocket"
                          : connectionStatus === 'reconnecting'
                          ? "Attempting to reconnect to live AI signals"
                          : signals.length > 0
                          ? "Using cached data from database - live connection unavailable"
                          : "No connection available and no cached data"
                        }
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {stats && (
                  <Badge variant="secondary" className="text-xs">
                    Last Updated: {new Date(stats.last_updated || Date.now()).toLocaleTimeString()}
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                AI-powered trading signals with real-time analysis
              </p>
              {stats && (
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <div className="flex items-center gap-1">
                    <Target className="w-4 h-4 text-primary" />
                    <span className="font-mono">{stats.total_signals} signals</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-success" />
                    <span className="font-mono">{stats.success_rate}% accuracy</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-warning" />
                    <span className="font-mono">{stats.sharpe_ratio?.toFixed(2)} sharpe</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Auto-refresh:</span>
              <Switch 
                checked={autoRefresh} 
                onCheckedChange={setAutoRefresh}
                size="sm"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
            </Button>
            
            <div className="flex border border-border rounded-lg p-1">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="mb-8">
          <SignalPerformanceSummary signals={signals} />
        </div>

        {/* Main Content */}
        <Tabs defaultValue="signals" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="signals" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Signals
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="models" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Models
            </TabsTrigger>
            <TabsTrigger value="risk" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Risk Analysis
            </TabsTrigger>
          </TabsList>

          {/* Signals Tab */}
          <TabsContent value="signals" className="space-y-6">
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {signals.map((signal) => (
                  <SignalCard
                    key={signal.id}
                    signal={signal}
                    onExecute={handleExecuteSignal}
                    onCancel={handleCancelSignal}
                  />
                ))}
              </div>
            ) : (
              <SignalTable
                signals={signals}
                totalCount={totalCount}
                page={page}
                perPage={perPage}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onPageChange={handlePageChange}
                onRefresh={refetch}
                loading={isLoading}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              />
            )}

            {signals.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No AI Signals Available</h3>
                <p className="text-muted-foreground mb-4">
                  {Object.keys(filters).length > 0 
                    ? "Try adjusting your filters to see more results."
                    : "AI signals will appear here when generated by our trading models."
                  }
                </p>
                {Object.keys(filters).length > 0 && (
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {isLoading && signals.length === 0 && (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading AI signals...</p>
              </div>
            )}
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <SignalPerformanceOverTimeChart />
              <WinRateByConfidenceChart />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <CumulativeReturnsChart />
              <SignalDistributionChart signals={signals} />
            </div>

            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Real-time Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-mono font-bold text-primary">
                        {stats.total_signals}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Signals</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-mono font-bold text-success">
                        {stats.success_rate}%
                      </p>
                      <p className="text-sm text-muted-foreground">Accuracy Rate</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-mono font-bold text-warning">
                        {stats.sharpe_ratio?.toFixed(2)}
                      </p>
                      <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-mono font-bold text-success">
                        {stats.avg_confidence}%
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Confidence</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-primary truncate">
                        {stats.top_performing_model}
                      </p>
                      <p className="text-sm text-muted-foreground">Top Model</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-muted-foreground">
                        {stats.last_updated ? new Date(stats.last_updated).toLocaleTimeString() : 'N/A'}
                      </p>
                      <p className="text-sm text-muted-foreground">Last Updated</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Models Tab */}
          <TabsContent value="models" className="space-y-6">
            <ModelPerformanceChart />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  LSTM vs XGBoost Performance Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                {modelsLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-6 h-6 text-primary animate-spin mx-auto mb-2" />
                    <p className="text-muted-foreground">Loading model data...</p>
                  </div>
                ) : models.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* LSTM Model */}
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Brain className="w-5 h-5 text-primary" />
                          <span className="font-semibold">LSTM Model</span>
                          <Badge variant="outline">v2.1</Badge>
                        </div>
                        <Badge variant="default" className="bg-success text-success-foreground">
                          Primary
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Accuracy Rate</span>
                          <span className="font-mono text-success">87.3%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                          <span className="font-mono text-warning">2.45</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Total Return</span>
                          <span className="font-mono text-success">+24.7%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Win Rate</span>
                          <span className="font-mono text-success">78.9%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Avg Holding Period</span>
                          <span className="font-mono">4.2 days</span>
                        </div>
                      </div>
                    </div>

                    {/* XGBoost Model */}
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Brain className="w-5 h-5 text-primary" />
                          <span className="font-semibold">XGBoost Model</span>
                          <Badge variant="outline">v1.8</Badge>
                        </div>
                        <Badge variant="secondary">
                          Secondary
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Accuracy Rate</span>
                          <span className="font-mono text-success">82.1%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                          <span className="font-mono text-warning">2.12</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Total Return</span>
                          <span className="font-mono text-success">+18.3%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Win Rate</span>
                          <span className="font-mono text-success">74.2%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Avg Holding Period</span>
                          <span className="font-mono">2.8 days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No model performance data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risk Analysis Tab */}
          <TabsContent value="risk" className="space-y-6">
            <RiskMetricsChart riskMetrics={riskMetrics} />
            
            {riskMetrics && riskMetrics.recommended_adjustments && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Portfolio Optimization Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {riskMetrics.recommended_adjustments.map((adjustment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border">
                        <div className="flex items-center gap-3">
                          <Badge variant={
                            adjustment.type === 'REDUCE' ? 'destructive' :
                            adjustment.type === 'INCREASE' ? 'default' :
                            'secondary'
                          }>
                            {adjustment.type}
                          </Badge>
                          <div>
                            <p className="font-medium">{adjustment.asset}</p>
                            <p className="text-sm text-muted-foreground">{adjustment.reason}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm">
                            {adjustment.current_allocation.toFixed(1)}% → {adjustment.recommended_allocation.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </PageLayout>
    </>
  );
}